import React from "react";
import { AuditLogDiff } from "@common/types/auditLog";
import {
    AccrualLineDelta,
    AccrualLineSnapshot,
    computeAccrualLineDeltas,
    toAccrualLineArray,
} from "@common/helpers/accrualLineDeltas";
import { sortLineItems } from "@common/helpers/lineItems";
import { AccrualLabelResolver, getLineLabel } from "./accrualValueFormat";
import { renderLineDeltaPhrase } from "./accrualActivityPhrases";

/**
 * Position of the line inside the document it belongs to — the "after" array for
 * additions and updates, the "before" array for removals — using the same order
 * as the line-item grids so the `#N` fallback label matches the row the user saw.
 */
function getDeltaLineLabel({
    delta,
    beforeLines,
    afterLines,
}: {
    delta: AccrualLineDelta;
    beforeLines: AccrualLineSnapshot[];
    afterLines: AccrualLineSnapshot[];
}): string {
    const sourceLines = delta.type === "removed" ? beforeLines : afterLines;
    return getLineLabel(
        delta.line,
        sourceLines.findIndex((line) => line.id === delta.id),
    );
}

export function describeLineDeltas({
    diff,
    currency,
    resolver,
}: {
    diff: AuditLogDiff;
    currency: string | null;
    resolver: AccrualLabelResolver;
}): React.ReactNode[] {
    const beforeLines = sortLineItems(toAccrualLineArray(diff.before.value));
    const afterLines = sortLineItems(toAccrualLineArray(diff.after.value));
    const deltas = computeAccrualLineDeltas(beforeLines, afterLines);

    return deltas.flatMap((delta) => {
        const phrase = renderLineDeltaPhrase({
            delta,
            label: getDeltaLineLabel({ delta, beforeLines, afterLines }),
            currency,
            resolver,
        });
        return phrase === null ? [] : [phrase];
    });
}
